import {useRef, useCallback, useState, useMemo, useEffect} from 'react';

export const useVideoControl = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  // Ensure visible state has updated when video method is called
  useEffect(() => {
    if (visible && pendingAction) {
      if (pendingAction === 'play' && videoRef.current) {
        videoRef.current.play();
      } else if (pendingAction === 'restart' && videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      setPendingAction(null);
    }
  }, [visible, pendingAction]);

  const play = useCallback(() => {
    setVisible(true); // Ensure video is visible when playing
    setPendingAction('play');
  }, []);

  const pause = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [videoRef]);

  const restart = useCallback(() => {
    setVisible(true); // Ensure video is visible when restarting
    setPendingAction('restart');
  }, []);

  const stop = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [videoRef]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  const show = useCallback(() => {
    setVisible(true);
  }, []);

  // Memoize the returned object to ensure a stable reference
  return useMemo(
    () => ({
      videoRef,
      play,
      pause,
      restart,
      stop,
      hide,
      show,
      visible,
    }),
    [videoRef, play, pause, restart, stop, hide, show, visible]
  );
};
